/**
 * RequestHistory Component
 *
 * Displays list of request history entries with timestamp, endpoint, and status code.
 * Allows selection of history entries for replay.
 *
 * Requirements: 10.4, 10.5, 11.1
 */

import React from 'react';
import { RequestHistoryEntry } from '../types';

export interface RequestHistoryProps {
  /** Array of history entries (newest first) */
  history: RequestHistoryEntry[];
  /** Callback when a history entry is selected for replay */
  onSelectEntry: (entry: RequestHistoryEntry) => void;
}

/**
 * RequestHistory component displays a list of past API test requests
 */
export const RequestHistory: React.FC<RequestHistoryProps> = ({ history, onSelectEntry }) => {
  if (history.length === 0) {
    return (
      <div>
        <h3
          className="text-gray-900 mb-4"
          style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-semibold)',
          }}
        >
          📜 Histórico
        </h3>
        <div
          className="text-center py-8"
          style={{
            backgroundColor: 'var(--color-gray-50)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-gray-200)',
          }}
        >
          <svg
            className="w-12 h-12 mx-auto mb-3"
            style={{ color: 'var(--color-gray-400)' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--color-gray-500)',
            }}
          >
            Nenhuma requisição ainda
          </p>
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-gray-400)',
              marginTop: 'var(--space-1)',
            }}
          >
            Envie uma requisição para vê-la aqui
          </p>
        </div>
      </div>
    );
  }

  // Limit display to 50 entries (as per requirement 10.5)
  const displayedHistory = history.slice(0, 50);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3
          className="text-gray-900"
          style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-semibold)',
          }}
        >
          📜 Histórico
        </h3>
        <span className="badge-modern badge-gray" style={{ fontSize: 'var(--text-xs)' }}>
          {displayedHistory.length}
        </span>
      </div>

      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {displayedHistory.map((entry) => {
          const statusBadgeClass = entry.response.success ? 'badge-success' : 'badge-error';
          const methodBadgeClass = getMethodBadgeClass(entry.request.endpoint.method);

          return (
            <button
              key={entry.id}
              onClick={() => onSelectEntry(entry)}
              className="w-full text-left p-4 bg-white border rounded-lg transition-all"
              style={{
                borderColor: 'var(--color-gray-200)',
                borderRadius: 'var(--radius-lg)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-primary-300)';
                e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-gray-200)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span
                    className={`badge-modern ${methodBadgeClass}`}
                    style={{ fontSize: 'var(--text-xs)' }}
                  >
                    {entry.request.endpoint.method}
                  </span>
                  <code
                    className="text-gray-900 truncate"
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-medium)',
                    }}
                  >
                    {entry.request.endpoint.path}
                  </code>
                </div>
                <span
                  className={`badge-modern ${statusBadgeClass} flex-shrink-0`}
                  style={{ fontSize: 'var(--text-xs)' }}
                >
                  {entry.response.statusCode}
                </span>
              </div>

              {/* Endpoint Summary */}
              {entry.request.endpoint.summary && (
                <p className="text-gray-600 mb-2 truncate" style={{ fontSize: 'var(--text-xs)' }}>
                  {entry.request.endpoint.summary}
                </p>
              )}

              {/* Metadata */}
              <div
                className="flex items-center justify-between text-gray-500"
                style={{ fontSize: 'var(--text-xs)' }}
              >
                <span className="flex items-center gap-1">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 10A4 4 0 106 2a4 4 0 000 8z"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6 3v2l1 1"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {formatTimestamp(entry.timestamp)}
                </span>
                <span className="flex items-center gap-1">
                  {entry.response.responseTime.toFixed(0)} ms
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Get badge class for HTTP method
 */
function getMethodBadgeClass(method: string): string {
  switch (method) {
    case 'GET':
      return 'badge-primary';
    case 'POST':
      return 'badge-success';
    case 'PUT':
    case 'PATCH':
      return 'badge-warning';
    case 'DELETE':
      return 'badge-error';
    default:
      return 'badge-gray';
  }
}

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
}
